/**
 * The bout bot's lock forms (duel v3 P3; rules in systems/duelLockOpen.ts). Kept out of systems/duelBot.ts: the bot's mind no
 * longer counts its lock moves as ready (one gate, `BOT_SKILL_GATES`); this system spends them itself, as punishes:
 * - punish: the foe is opened (staggered, guard broken, in a recovery or stop) and in the lock move's reach -> the lock move,
 *   more surely and sooner at higher levels (its reaction time, `DUEL_REACT`, then a roll by `DUEL_SKILL`);
 * - escape: held in a form, on the white flash, it tries to break out by level (never at 0 stars, about half at 4).
 * Its draws come from a hash of the world tick and the bot's id, not the World's `Rng`.
 */
import { DUEL_FLAGS, DUEL_REACT, DUEL_SKILL } from '../data/duelMatch';
import { SKILLS } from '../data/skills';
import { hash2 } from '../rng';
import type { Entity } from '../world';
import { BOT_SKILL_GATES } from './duelBot';
import { duelLockOf } from './duelLock';
import { duelLockOpenOf, isOpened } from './duelLockOpen';
import { duelMatchOf } from './duelMatch';
import { botSeatOf } from './botSeat'; // STUDIO-BOTDUEL: a bot-vs-bot seat (systems/botBout.ts)
import { duelMoveOf } from './duelMove';
import { duelRecoveryOf } from './duelRecovery';
import { skillBarOf } from './skillBar';
import { slideOf } from './slide';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelBotLockSystem>();
export const duelBotLockOf = (w: object): DuelBotLockSystem | null => SYSTEMS.get(w) ?? null;

/** Seconds between the bot's lock thoughts. */
const THINK = 0.1;
/** Chance (by level 0-4) the bot tries to break out on the flash. */
export const BOT_ESCAPE: readonly number[] = [0, 0.15, 0.3, 0.42, 0.55];

// The bot's general mind leaves lock moves to this system while it runs.
BOT_SKILL_GATES.push((w, _botId, id) => {
  const sys = duelBotLockOf(w);
  return !(DUEL_FLAGS.v2 && sys && !sys.off && SKILLS[id]?.lockForm);
});

export class DuelBotLockSystem implements WorldSystem {
  readonly name = 'duelBotLock';
  /** Off: the bot's lock moves go back to its general mind (captures that drive the bot by hand). */
  off = false;
  private nextT = 0;
  private n = 0;
  /** World time the foe was first seen opened (the reaction counts from it), -1: not opened. */
  private openedAt = -1;
  /** The caster of the form the bot already chose about (escape or not). */
  private judged = -1;
  /** The bot's latest lock press (tests, the picture). */
  latest: { skillId: string; at: number } | null = null;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  step(_dt: number): void {
    const w = this.h.world;
    const st = botSeatOf(w) ?? duelMatchOf(w)?.current;
    if (this.off || !DUEL_FLAGS.v2 || !st || st.pvp || st.phase !== 'fight' || st.paused || !w.pvpBout?.fight) {
      this.openedAt = -1;
      return;
    }
    if (w.time < this.nextT) return;
    this.nextT = w.time + THINK;
    const bot = w.get(st.foeId);
    const foe = w.get(st.meId);
    if (!bot || !foe || bot.dead || foe.dead) return;
    const lv = Math.max(0, Math.min(DUEL_REACT.length - 1, st.level));
    const rnd = () => hash2(Math.round(w.time * 60), bot.id * 613 + this.n++);
    const open = duelLockOpenOf(w);
    const lock = duelLockOf(w);

    // Held: break out on the flash, by level, once a form.
    const v = open?.formView(bot.id);
    if (v && v.target === bot.id) {
      if (this.judged !== v.caster && v.escapable && v.t >= v.flash[0] && v.t <= v.flash[1]) {
        this.judged = v.caster;
        if (rnd() < BOT_ESCAPE[lv]!) open!.tryEscape(bot.id);
      }
      return;
    }
    this.judged = -1;
    if (lock?.inForm(bot.id)) return;

    // The punish: an opened foe in reach of a ready lock move.
    if (!isOpened(w, foe) || lock?.inForm(foe.id)) {
      this.openedAt = -1;
      return;
    }
    if (this.openedAt < 0) this.openedAt = w.time;
    if (w.time - this.openedAt < DUEL_REACT[lv]! || !this.free(bot)) return;
    const bar = skillBarOf(w.players.get(bot.id)?.progress ?? w.progress);
    const slot = bar.findIndex((id) => {
      const s = id ? SKILLS[id] : undefined;
      return !!s?.lockForm && (bot.cooldowns[s.id] ?? 0) <= 0 && bot.qi >= s.qi;
    });
    if (slot < 0) return;
    const s = SKILLS[bar[slot]!]!;
    const d = Math.hypot(foe.x - bot.x, foe.y - bot.y);
    // (the general mind closes the distance; a step short of reach the bot walks in for it)
    if (d > (s.range || 2) * 0.95) {
      if (d < (s.range || 2) + 1.5) w.apply({ t: 'moveDir', dx: (foe.x - bot.x) / d, dy: (foe.y - bot.y) / d }, bot.id);
      return;
    }
    if (rnd() >= DUEL_SKILL[lv]!) return;
    w.apply({ t: 'moveDir', dx: 0, dy: 0 }, bot.id);
    w.apply({ t: 'skill', slot }, bot.id);
    if (bot.cast || (bot.cooldowns[s.id] ?? 0) > 0) this.latest = { skillId: s.id, at: w.time };
  }

  /** On the ground, not hurt, casting, stepping, sliding or in a stop. */
  private free(e: Entity): boolean {
    const w = this.h.world;
    if (e.z > 0 || e.stunT > 0 || e.cast || e.dashT > 0 || e.swingT > 0 || slideOf(e)) return false;
    return (duelRecoveryOf(w)?.left(e.id) ?? 0) <= 0 && (duelMoveOf(w)?.lockLeft(e.id) ?? 0) <= 0;
  }
}
